/**
 * NOLAS 2026, (c) Mikaël FOURNY.
 * src/pwa/rechargerVersionRecente.ts
 *
 * Rôle : recharger la page SUR LA NOUVELLE VERSION, et pas sur l'ancienne.
 *
 * Le défaut corrigé (banc d'intégration du 2026-10-04) : la surveillance de
 * `index.html` (versionWatch.ts) voyait la nouvelle version, affichait le
 * bandeau, et son bouton « Recharger » faisait `location.reload()`. Or tant
 * que l'ancien service worker contrôle la page, un rechargement ressert la
 * page depuis son précache : la personne revenait sur l'ancienne version, à
 * chaque fois, et le rechargement automatique à l'ouverture tournait à vide.
 * C'est exactement le symptôme de l'iPhone de Mika.
 *
 * Ce qui est fait ici, dans l'ordre :
 *   1. demander au navigateur de vérifier le service worker (update) ;
 *   2. si une version attend (ou s'installe), lui envoyer SKIP_WAITING, le
 *      message que notre sw.js comprend, puis recharger dès qu'elle a pris
 *      la main (controllerchange) ;
 *   3. sinon, ou si rien ne se passe en quelques secondes, recharger quand
 *      même : la page n'est jamais bloquée sur un bouton qui ne fait rien.
 *   Un seul rechargement, quoi qu'il arrive.
 *
 * Mikael - chef de projet - 2026-10-04
 *
 * 2026-10-07 (Mika : « "Recharger" ne vient jamais sur mon iPhone, je reste
 * toujours sur l'ancienne application ») : le rechargement de SECOURS de
 * l'étape 3 était un `location.reload()`, donc servi par l'ANCIEN service
 * worker depuis son précache : sur iPhone, où l'installation de la nouvelle
 * version (précache de plusieurs Mo) dépasse souvent les 4 s, la page
 * revenait sur l'ancienne version, la garde anti-boucle empêchait tout
 * nouvel essai, et la surveillance ne relisait plus rien (signal unique).
 * Désormais, quand l'appelant SAIT qu'une autre version est en ligne
 * (`force`), le secours ne passe plus par l'ancien service worker :
 * réseau vérifié, service worker désenregistré, caches de l'application
 * (Cache Storage) vidés, puis `location.replace` avec un paramètre
 * anti-cache. Ni localStorage, ni sessionStorage, ni IndexedDB (session,
 * jeton CSRF, file hors ligne) ne sont touchés. Hors ligne : simple
 * rechargement, jamais une page blanche. Garde : pas deux réinstallations en
 * deux minutes. Mikael - chef de projet - 2026-10-07
 */

/** Délai au-delà duquel on recharge sans attendre la prise de main. */
export const DELAI_SECOURS_MS = 4000;
/** 2026-10-07 : délai de secours quand une autre version est SÛREMENT en ligne. */
export const DELAI_SECOURS_FORCE_MS = 6000;
/** Paramètre anti-cache posé par la réinstallation, retiré au démarrage. */
export const PARAM_MAJ = 'maj';
/** Garde : pas deux réinstallations dans ce délai (sessionStorage). */
// Flotte : clé neutre, sans nom d'événement. Mikael - chef de projet - 2026-10-15
export const CLE_GARDE_REINSTALLATION = 'app.maj.reinstallation';
const DELAI_GARDE_REINSTALLATION_MS = 2 * 60 * 1000;

export type Dependances = {
  readonly sw: ServiceWorkerContainer | undefined;
  readonly recharger: () => void;
  readonly planifier: (f: () => void, ms: number) => unknown;
  /**
   * 2026-10-07 : secours qui ne passe pas par l'ancien service worker
   * (défaut : reinstallerSansAncienServiceWorker). Absent : `recharger`.
   */
  readonly reinstaller?: () => void | Promise<void>;
};

export interface OptionsRechargement {
  /**
   * Vrai quand l'appelant a CONSTATÉ qu'une autre version est en ligne
   * (index.html relu) : le secours réinstalle au lieu de recharger sous
   * l'ancien service worker. Mikael - chef de projet - 2026-10-07
   */
  readonly force?: boolean;
}

function dependancesParDefaut(): Dependances {
  return {
    sw: typeof navigator !== 'undefined' && 'serviceWorker' in navigator ? navigator.serviceWorker : undefined,
    recharger: () => window.location.reload(),
    planifier: (f, ms) => window.setTimeout(f, ms),
    reinstaller: reinstallerSansAncienServiceWorker,
  };
}

/**
 * Recharge SANS l'ancien service worker : réseau vérifié (index.html relu
 * sans cache), inscriptions retirées, Cache Storage de l'application vidé,
 * puis `location.replace` avec `?maj=<instant>`. Hors ligne, ou garde
 * posée il y a moins de deux minutes : simple rechargement.
 * Mikael - chef de projet - 2026-10-07
 */
export async function reinstallerSansAncienServiceWorker(): Promise<void> {
  try {
    const avant = Number(window.sessionStorage.getItem(CLE_GARDE_REINSTALLATION));
    if (Number.isFinite(avant) && avant > 0 && Date.now() - avant < DELAI_GARDE_REINSTALLATION_MS) {
      window.location.reload();
      return;
    }
  } catch {
    // Stockage refusé : on continue, la réinstallation ne boucle pas d'elle-même
    // (la page rechargée est la nouvelle version, sans service worker).
  }
  let enLigne = false;
  try {
    // `?verif=` : contourne le précache de l'ancien service worker (voir
    // versionWatch.ts, adresseIndexEnLigne). Mikael - chef de projet - 2026-10-07
    const u = new URL('index.html', document.baseURI);
    u.searchParams.set('verif', String(Date.now()));
    const r = await fetch(u.href, { cache: 'no-store', credentials: 'same-origin' });
    enLigne = r.ok;
  } catch {
    enLigne = false;
  }
  if (!enLigne) {
    window.location.reload();
    return;
  }
  try {
    window.sessionStorage.setItem(CLE_GARDE_REINSTALLATION, String(Date.now()));
  } catch {
    /* sans garde */
  }
  try {
    if ('serviceWorker' in navigator) {
      const inscriptions = await navigator.serviceWorker.getRegistrations();
      await Promise.all(inscriptions.map((i) => i.unregister().catch(() => false)));
    }
  } catch {
    /* on recharge quand même */
  }
  try {
    if (typeof caches !== 'undefined') {
      const cles = await caches.keys();
      await Promise.all(cles.map((c) => caches.delete(c)));
    }
  } catch {
    /* on recharge quand même */
  }
  const u = new URL(window.location.href);
  u.searchParams.set(PARAM_MAJ, String(Date.now()));
  window.location.replace(u.toString());
}

/**
 * Retire `?maj=…` de l'adresse au démarrage (sans recharger), pour qu'il ne
 * reste ni dans l'historique ni dans un lien partagé. Mikael - chef de projet - 2026-10-07
 */
export function retirerParametreMaj(): void {
  try {
    const u = new URL(window.location.href);
    if (!u.searchParams.has(PARAM_MAJ)) return;
    u.searchParams.delete(PARAM_MAJ);
    window.history.replaceState(window.history.state, '', u.pathname + u.search + u.hash);
  } catch {
    /* sans effet */
  }
}

export async function rechargerVersionRecente(
  dep: Dependances = dependancesParDefaut(),
  options: OptionsRechargement = {},
): Promise<void> {
  const force = options.force === true;
  let fait = false;
  const uneFois = (): void => {
    if (!fait) {
      fait = true;
      dep.recharger();
    }
  };
  // Secours : sous `force`, jamais par l'ancien service worker.
  const secours = (): void => {
    if (fait) return;
    if (force && dep.reinstaller !== undefined) {
      fait = true;
      void dep.reinstaller();
      return;
    }
    uneFois();
  };

  const sw = dep.sw;
  if (sw === undefined) {
    uneFois();
    return;
  }
  try {
    const reg = await sw.getRegistration();
    if (reg === undefined) {
      uneFois();
      return;
    }
    try {
      await reg.update();
    } catch {
      // Hors ligne ou serveur indisponible : on travaille avec ce qu'on a.
    }
    const enAttente = reg.waiting ?? reg.installing;
    if (enAttente === null) {
      // 2026-10-07 : une autre version est en ligne mais aucune n'attend :
      // un rechargement sous l'ancien service worker ressortirait l'ancienne.
      if (force && sw.controller) {
        secours();
        return;
      }
      uneFois();
      return;
    }
    sw.addEventListener('controllerchange', uneFois);
    const envoyer = (w: ServiceWorker): void => w.postMessage({ type: 'SKIP_WAITING' });
    if (reg.waiting !== null) {
      envoyer(reg.waiting);
    } else {
      enAttente.addEventListener('statechange', () => {
        if (enAttente.state === 'installed') envoyer(enAttente);
      });
    }
    dep.planifier(secours, force ? DELAI_SECOURS_FORCE_MS : DELAI_SECOURS_MS);
  } catch {
    secours();
  }
}
